import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Plus, Trash2, Star, Mail } from "lucide-react";
import { RetainerPanel } from "./RetainerPanel";

// Beast V1 Checkpoint 4 — Customer detail drawer.
// Lightweight. Display Name / Commercial Mode / Preferred Language /
// Internal Notes / Contacts CRUD. No CRM segmentation, no scoring.
const COMMERCIAL_MODES = [
  { value: "standard", label: "Standard" },
  { value: "managed", label: "Managed" },
  { value: "retainer", label: "Retainer" },
];

const LANGUAGES = [
  { value: "", label: "—" },
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "nl", label: "Dutch" },
  { value: "pap", label: "Papiamento" },
];

export const CustomerDetailDrawer = ({ customerId, onClose, onChanged }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({
    display_name: "",
    commercial_mode: "standard",
    preferred_language: "",
    notes: "",
  });
  const [newContact, setNewContact] = useState({
    name: "",
    email: "",
    phone: "",
    is_primary: false,
  });
  // Per-contact "add email" input + validation error. Keyed by contact
  // id so a duplicate-email failure on one contact doesn't leak into
  // another row's UI.
  const [emailDrafts, setEmailDrafts] = useState({});
  const [emailErrors, setEmailErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/customers/${customerId}`);
      setCustomer(data);
      setDraft({
        display_name: data.display_name || "",
        commercial_mode: data.commercial_mode || "standard",
        preferred_language: data.preferred_language || "",
        notes: data.notes || "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load customer");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveCustomer = async () => {
    setBusy(true);
    try {
      const payload = {
        display_name: draft.display_name,
        commercial_mode: draft.commercial_mode,
        preferred_language: draft.preferred_language || null,
        notes: draft.notes,
      };
      const { data } = await api.patch(`/admin/customers/${customerId}`, payload);
      setCustomer(data);
      toast.success("Customer saved");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const addContact = async () => {
    if (!newContact.email.trim()) {
      toast.error("Email is required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/customers/${customerId}/contacts`, {
        name: newContact.name.trim(),
        email: newContact.email.trim(),
        phone: newContact.phone.trim(),
        is_primary: !!newContact.is_primary,
      });
      setNewContact({ name: "", email: "", phone: "", is_primary: false });
      await load();
      onChanged?.();
    } catch (e) {
      // Surface the 409 duplicate cleanly per the spec.
      toast.error(e?.response?.data?.detail || "Failed to add contact");
    } finally {
      setBusy(false);
    }
  };

  const patchContact = async (id, partial) => {
    setBusy(true);
    try {
      await api.patch(`/admin/contacts/${id}`, partial);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteContact = async (id) => {
    if (!confirm("Delete this contact?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/contacts/${id}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const addEmailToContact = async (contactId) => {
    const value = (emailDrafts[contactId] || "").trim();
    if (!value) {
      setEmailErrors((prev) => ({ ...prev, [contactId]: "Enter an email address" }));
      return;
    }
    // Clear any prior error before we try.
    setEmailErrors((prev) => ({ ...prev, [contactId]: "" }));
    setBusy(true);
    try {
      await api.post(`/admin/contacts/${contactId}/emails`, { email: value });
      setEmailDrafts((prev) => ({ ...prev, [contactId]: "" }));
      await load();
      onChanged?.();
    } catch (e) {
      // 409 duplicate on another contact → INLINE error, drawer stays open.
      const detail = e?.response?.data?.detail || "Failed to add email";
      setEmailErrors((prev) => ({ ...prev, [contactId]: detail }));
    } finally {
      setBusy(false);
    }
  };

  const removeEmailFromContact = async (contactId, email) => {
    setBusy(true);
    try {
      const encoded = encodeURIComponent(email);
      await api.delete(`/admin/contacts/${contactId}/emails/${encoded}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove email");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="customer-detail-drawer"
      className="fixed inset-0 z-[9998] bg-black/70 flex justify-end"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl h-full bg-[#0F0F0F] border-l border-[#262626] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[#0F0F0F] border-b border-[#1F1F1F] px-6 py-4 flex items-center justify-between">
          <div className="ur-eyebrow">Customer</div>
          <button
            data-testid="customer-detail-close"
            onClick={onClose}
            className="w-9 h-9 rounded-full border border-[#262626] grid place-items-center hover:border-[#FF6600]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading || !customer ? (
          <div className="p-6 text-sm text-[#A3A3A3] flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="p-6 space-y-5">
            <label className="text-xs text-[#A3A3A3] block">
              Display Name
              <input
                data-testid="customer-name-input"
                className="ur-input mt-1 w-full"
                value={draft.display_name}
                onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
              />
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="text-xs text-[#A3A3A3] block">
                Commercial Mode
                <select
                  data-testid="customer-commercial-mode"
                  className="ur-input mt-1 w-full"
                  value={draft.commercial_mode}
                  onChange={(e) => setDraft({ ...draft, commercial_mode: e.target.value })}
                >
                  {COMMERCIAL_MODES.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-[#A3A3A3] block">
                Preferred Language
                <select
                  data-testid="customer-language"
                  className="ur-input mt-1 w-full"
                  value={draft.preferred_language || ""}
                  onChange={(e) => setDraft({ ...draft, preferred_language: e.target.value })}
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="text-xs text-[#A3A3A3] block">
              Internal Notes
              <textarea
                data-testid="customer-notes"
                rows={3}
                className="ur-input mt-1 w-full"
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </label>

            <div className="flex justify-end">
              <button
                data-testid="customer-save"
                onClick={saveCustomer}
                disabled={busy}
                className="ur-btn-primary text-sm"
              >
                Save
              </button>
            </div>

            {/* Retainer sub-panel — rendered only when the CURRENT
                (saved) commercial_mode is retainer. Uses the SAVED
                customer, not the draft, so switching the dropdown
                doesn't briefly render an unconfigured retainer view. */}
            {customer.commercial_mode === "retainer" && (
              <RetainerPanel
                customerId={customerId}
                initialTerms={customer.retainer_terms}
                onChanged={onChanged}
              />
            )}

            {/* Contacts */}
            <div>
              <div className="ur-eyebrow mb-2">Contacts</div>
              <div className="space-y-2" data-testid="customer-contacts-list">
                {(customer.contacts || []).map((c) => {
                  const emails = c.emails && c.emails.length > 0 ? c.emails : (c.email ? [c.email] : []);
                  const draftEmail = emailDrafts[c.id] || "";
                  const errorMsg = emailErrors[c.id] || "";
                  return (
                    <div
                      key={c.id}
                      data-testid={`contact-row-${c.id}`}
                      className="bg-[#141414] border border-[#1F1F1F] rounded-lg p-3 space-y-3"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input
                          data-testid={`contact-name-${c.id}`}
                          className="ur-input text-sm"
                          placeholder="Name"
                          defaultValue={c.name}
                          onBlur={(e) => {
                            if (e.target.value !== c.name) patchContact(c.id, { name: e.target.value });
                          }}
                        />
                        <input
                          data-testid={`contact-phone-${c.id}`}
                          className="ur-input text-sm"
                          placeholder="Phone"
                          defaultValue={c.phone}
                          onBlur={(e) => {
                            if (e.target.value !== c.phone) patchContact(c.id, { phone: e.target.value });
                          }}
                        />
                      </div>

                      {/* Emails — chip list. Contacts may hold many
                          addresses; every one resolves back to this
                          same Contact + Customer + Commercial Mode. */}
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#5A5A5A] mb-1 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> Emails
                        </div>
                        {/* Legacy testid preserved: `contact-email-<id>` still
                            resolves to the primary email cell so any regression
                            harness looking for it keeps working. */}
                        <div
                          data-testid={`contact-emails-${c.id}`}
                          className="flex flex-wrap gap-2"
                        >
                          {emails.map((em, idx) => (
                            <span
                              key={em}
                              data-testid={idx === 0 ? `contact-email-${c.id}` : `contact-email-${c.id}-${idx}`}
                              className="inline-flex items-center gap-1.5 rounded-full bg-[#0F0F0F] border border-[#262626] px-2.5 py-1 text-xs"
                            >
                              <span className="truncate max-w-[220px]">{em}</span>
                              {emails.length > 1 && (
                                <button
                                  type="button"
                                  data-testid={`contact-email-remove-${c.id}-${idx}`}
                                  onClick={() => removeEmailFromContact(c.id, em)}
                                  className="w-4 h-4 rounded-full grid place-items-center text-[#A3A3A3] hover:text-red-400"
                                  title="Remove this email"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>

                        <div className="mt-2 flex items-center gap-2">
                          <input
                            data-testid={`contact-add-email-${c.id}`}
                            type="email"
                            className="ur-input text-sm flex-1"
                            placeholder="Add another email"
                            value={draftEmail}
                            onChange={(e) => {
                              setEmailDrafts((prev) => ({ ...prev, [c.id]: e.target.value }));
                              // Clear the error the moment the user edits.
                              if (errorMsg) setEmailErrors((prev) => ({ ...prev, [c.id]: "" }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addEmailToContact(c.id);
                              }
                            }}
                          />
                          <button
                            type="button"
                            data-testid={`contact-add-email-submit-${c.id}`}
                            onClick={() => addEmailToContact(c.id)}
                            disabled={busy || !draftEmail.trim()}
                            className="ur-btn-secondary text-xs py-2 px-3 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Plus className="w-3.5 h-3.5" /> Add
                          </button>
                        </div>
                        {errorMsg && (
                          <div
                            data-testid={`contact-add-email-error-${c.id}`}
                            className="mt-1 text-[11px] text-red-400"
                          >
                            {errorMsg}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <label className="text-[11px] text-[#A3A3A3] inline-flex items-center gap-1">
                          <input
                            data-testid={`contact-primary-${c.id}`}
                            type="checkbox"
                            checked={!!c.is_primary}
                            onChange={(e) => patchContact(c.id, { is_primary: e.target.checked })}
                          />
                          <Star className="w-3 h-3" /> Primary
                        </label>
                        <button
                          data-testid={`contact-delete-${c.id}`}
                          onClick={() => deleteContact(c.id)}
                          className="w-8 h-8 rounded-full border border-[#262626] grid place-items-center hover:border-red-500"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {(customer.contacts || []).length === 0 && (
                  <div className="text-xs text-[#A3A3A3] py-2">No contacts yet.</div>
                )}
              </div>

              {/* Add contact */}
              <div className="mt-3 bg-[#141414] border border-[#1F1F1F] rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#5A5A5A] mb-2">Add Contact</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    data-testid="new-contact-name"
                    className="ur-input text-sm"
                    placeholder="Name"
                    value={newContact.name}
                    onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                  />
                  <input
                    data-testid="new-contact-email"
                    className="ur-input text-sm"
                    placeholder="Email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  />
                  <input
                    data-testid="new-contact-phone"
                    className="ur-input text-sm"
                    placeholder="Phone"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  />
                  <label className="text-[11px] text-[#A3A3A3] inline-flex items-center gap-2 px-2">
                    <input
                      data-testid="new-contact-primary"
                      type="checkbox"
                      checked={!!newContact.is_primary}
                      onChange={(e) =>
                        setNewContact({ ...newContact, is_primary: e.target.checked })
                      }
                    />
                    Primary
                  </label>
                </div>
                <div className="flex justify-end mt-2">
                  <button
                    data-testid="new-contact-add"
                    onClick={addContact}
                    disabled={busy}
                    className="ur-btn-primary text-xs py-2 px-3"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Contact
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
